import React, { Fragment } from 'react';
import classnames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';

// Components
import { Button } from 'dibs-elements/exports/Button';
import Close from 'dibs-icons/exports/Close';

import { CREATOR_UNKNOWN } from '../constants';
import { QuickViewItem_item as QuickViewItemProp } from './__generated__/QuickViewItem_item.graphql';

// Styles
import styles from './styles.scss';

type QuickViewItemProps = {
    item: QuickViewItemProp;
    isQuickViewActive: boolean;
    onClose: () => void;
    onViewDetailsClick: () => void;
    isMobile?: boolean;
    isSbTile?: boolean;
};

type QuickViewItemState = {
    isQuickViewActive: boolean;
};

type Paragraph = {
    key: string;
    text: string | null;
    mobileText: string | null;
};

import { FormattedMessage } from 'react-intl';

export const copy = {
    by: (
        <FormattedMessage id="dc.searchProductTile.by" defaultMessage="By">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),
    locatedIn: (
        <FormattedMessage id="dc.searchProductTile.locatedIn" defaultMessage="Located in">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),
    materials: (
        <FormattedMessage id="dc.searchProductTile.materials" defaultMessage="Materials">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),
    category: (
        <FormattedMessage id="dc.searchProductTile.category" defaultMessage="Category">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),

    viewDetails: (
        <FormattedMessage id="dc.searchProductTile.viewDetails" defaultMessage="View Full Details">
            {msg => <Fragment>{msg}</Fragment>}
        </FormattedMessage>
    ),
};

class QuickViewItem extends React.Component<QuickViewItemProps, QuickViewItemState> {
    constructor(props: QuickViewItemProps) {
        super(props);

        this.state = {
            isQuickViewActive: false,
        };
    }

    componentDidUpdate(prevProps: QuickViewItemProps): void {
        const { isQuickViewActive } = this.props;
        if (prevProps.isQuickViewActive !== isQuickViewActive) {
            this.setState({ isQuickViewActive });
        }
    }

    render(): React.ReactNode {
        const { item, onClose, onViewDetailsClick, isMobile, isSbTile } = this.props;
        const { isQuickViewActive } = this.state;
        /* istanbul ignore if */
        if (!item.quickViewDisplay) {
            return null;
        }

        const pdpUrl = get(item, 'localizedPdpUrl') || '';
        const mobileTitle = get(item, 'quickViewDisplay.mobileTitle');
        const title = get(item, isSbTile ? 'title' : 'quickViewDisplay.title');
        const paragraphs = get(item, 'quickViewDisplay.paragraphs') || [];
        const quickViewClass = classnames(styles.wrapper, {
            [styles.isActive]: isQuickViewActive,
            [styles.isSbTile]: isSbTile,
        });
        const itemClass = classnames(styles.item, {
            [styles.isSbTile]: isSbTile,
        });

        const paragraphsMap = paragraphs.reduce(
            (map: { [x: string]: string | null }, paragraph: Paragraph) => {
                const { key, text, mobileText } = paragraph;
                map[key] = isMobile ? mobileText : text;
                return map;
            },
            {}
        );

        const { creators, location, description, materials, attributes } = paragraphsMap;
        const shouldShowCreatorName = !!creators && creators.toLowerCase() !== CREATOR_UNKNOWN;

        return (
            <div className={quickViewClass}>
                <div className={itemClass}>
                    <div className={styles.close} onClick={onClose}>
                        <Close />
                    </div>
                    {isSbTile ? (
                        <div className={styles.testContainer}>
                            <div className={styles.testParagraphs}>
                                {!isMobile && <div className={styles.testTitle}>{title}</div>}
                                {shouldShowCreatorName && (
                                    <div className={styles.testCreator}>
                                        {copy.by} {creators}
                                        {location && (
                                            <div>
                                                {copy.locatedIn} {location}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {description && (
                                    <div className={styles.testDescription}>{description}</div>
                                )}
                                {attributes && (
                                    <div className={styles.testParagraph}>
                                        {copy.category}
                                        <h3 className={styles.testCategory}>{attributes}</h3>
                                    </div>
                                )}
                                {materials && (
                                    <div className={styles.testParagraph}>
                                        {copy.materials}
                                        <p>{materials}</p>
                                    </div>
                                )}
                            </div>
                            <div>
                                {isMobile ? (
                                    <a rel="nofollow" href={pdpUrl} onClick={onViewDetailsClick}>
                                        {copy.viewDetails}
                                    </a>
                                ) : (
                                    <Button
                                        fullWidth
                                        size="small"
                                        type={'secondary'}
                                        href={pdpUrl}
                                        onClick={onViewDetailsClick}
                                        dataTn="quick-view-item-button"
                                    >
                                        {copy.viewDetails}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.content}>
                            <div className={styles.title}>{isMobile ? mobileTitle : title}</div>
                            {!!paragraphs &&
                                paragraphs.map((paragraph: Paragraph) => (
                                    <div className={styles.paragraph} key={paragraph.key}>
                                        {isMobile ? paragraph.mobileText : paragraph.text}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default createFragmentContainer(QuickViewItem, {
    item: graphql`
        fragment QuickViewItem_item on Item {
            title
            localizedPdpUrl
            quickViewDisplay {
                title
                mobileTitle: title(formatType: MOBILE)
                paragraphs {
                    key
                    text
                    mobileText: text(formatType: MOBILE)
                }
            }
        }
    `,
});
